import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import {
  useAccountsDashboard,
  useAccountsOfficerWorkbench,
} from "../hooks";
import type {
  AccountsDashboardResponseDto,
  AccountsOfficerWorkbenchResponseDto,
} from "../accounts.types";

const AccountsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isHeadView = staffSubRole === "CEO" || staffSubRole === "ACCOUNTS_HEAD";
  const isOfficerView = staffSubRole === "ACCOUNTS_OFFICER";

  const headQuery = useAccountsDashboard({
    enabled: !isOfficerView,
  });
  const officerQuery = useAccountsOfficerWorkbench({
    enabled: isOfficerView,
  });

  if (isOfficerView) {
    const { data, isLoading, error } = officerQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="Accounts Officer Workbench"
          message="Loading assigned pricing and billing tasks…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="Accounts Officer Workbench"
          message="Unable to load accounts data. Please try again."
        />
      );
    }

    const workbench: AccountsOfficerWorkbenchResponseDto = {
      pricingTasks: {
        pendingPricingApprovals:
          data.pricingTasks?.pendingPricingApprovals ?? 0,
        expiringPricingProfiles:
          data.pricingTasks?.expiringPricingProfiles ?? 0,
        highImpactChanges: data.pricingTasks?.highImpactChanges ?? 0,
      },
      billingTasks: {
        pendingBillingActivations:
          data.billingTasks?.pendingBillingActivations ?? 0,
        overdueInvoices: data.billingTasks?.overdueInvoices ?? 0,
        billingDisputes: data.billingTasks?.billingDisputes ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="Accounts Officer Workbench"
          message="Assigned pricing approvals, invoice reviews, and billing issue resolution."
        />

        <SectionCard title="Pricing tasks">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Pending pricing approvals"
              value={workbench.pricingTasks.pendingPricingApprovals}
            />
            <StatCard
              label="Expiring pricing profiles"
              value={workbench.pricingTasks.expiringPricingProfiles}
            />
            <StatCard
              label="High-impact changes"
              value={workbench.pricingTasks.highImpactChanges}
            />
          </div>
        </SectionCard>

        <SectionCard title="Billing tasks">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Pending billing activations"
              value={workbench.billingTasks.pendingBillingActivations}
            />
            <StatCard
              label="Overdue invoices"
              value={workbench.billingTasks.overdueInvoices}
            />
            <StatCard
              label="Billing disputes"
              value={workbench.billingTasks.billingDisputes}
            />
          </div>
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title={isHeadView ? "Accounts Head Dashboard" : "Accounts Dashboard"}
        message="Loading pricing and billing overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title={isHeadView ? "Accounts Head Dashboard" : "Accounts Dashboard"}
        message="Unable to load accounts data. Please try again."
      />
    );
  }

  const dashboard: AccountsDashboardResponseDto = {
    kpis: {
      activePricingProfiles: data.kpis?.activePricingProfiles ?? 0,
      pendingPricingApprovals: data.kpis?.pendingPricingApprovals ?? 0,
      activeBillingProfiles: data.kpis?.activeBillingProfiles ?? 0,
      overdueInvoices: data.kpis?.overdueInvoices ?? 0,
      disputedInvoices: data.kpis?.disputedInvoices ?? 0,
    },
    pricing: {
      providerPricingProfiles:
        data.pricing?.providerPricingProfiles ?? 0,
      aiPlanPricingProfiles: data.pricing?.aiPlanPricingProfiles ?? 0,
      discountProfiles: data.pricing?.discountProfiles ?? 0,
    },
    billing: {
      activeBillingProfiles: data.billing?.activeBillingProfiles ?? 0,
      pendingBillingActivations:
        data.billing?.pendingBillingActivations ?? 0,
      highRiskBillingProfiles: data.billing?.highRiskBillingProfiles ?? 0,
    },
  };

  const { kpis, pricing, billing } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title={isHeadView ? "Accounts Head Dashboard" : "Accounts Dashboard"}
        message="Pricing, billing, and accounting operations."
      />

      <SectionCard title="Accounts KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Active pricing profiles"
            value={kpis.activePricingProfiles}
            helpText={`Pending approvals: ${kpis.pendingPricingApprovals}`}
          />
          <StatCard
            label="Active billing profiles"
            value={kpis.activeBillingProfiles}
            helpText={`Overdue invoices: ${kpis.overdueInvoices}`}
          />
          <StatCard
            label="Disputed invoices"
            value={kpis.disputedInvoices}
            helpText="Invoices or charges needing review"
          />
        </div>
      </SectionCard>

      <SectionCard title="Pricing & plans">
        <InfoCard
          title="Plan governance"
          body={`Provider pricing profiles: ${pricing.providerPricingProfiles}, AI plan pricing profiles: ${pricing.aiPlanPricingProfiles}, Discount profiles: ${pricing.discountProfiles}.`}
        />
      </SectionCard>

      <SectionCard title="Billing operations">
        <InfoCard
          title="Billing runs & issues"
          body={`Active billing profiles: ${billing.activeBillingProfiles}, Pending billing activations: ${billing.pendingBillingActivations}, High-risk billing profiles: ${billing.highRiskBillingProfiles}.`}
        />
      </SectionCard>
    </div>
  );
};

export default AccountsDashboardPage;